import { createContext, PropsWithChildren, useContext, useMemo, useRef } from "react"
import { TimelineCalendarEvent, TimelineCalendarResource } from "../calendar/fullCalendarTypes"
import type { DaySourceEntry } from "../resourceBookingCalendars/timelineHooks"

const TimelineContext = createContext<{
    timelineResources: TimelineCalendarResource[]
    allServiceCenterIdsForEvents: readonly string[]
    resourceBufferMap: Record<string, number>
    eventIndexRef: React.MutableRefObject<Record<string, TimelineCalendarEvent[]>>
    sourceEventIdsRef: React.MutableRefObject<Record<string, ReadonlySet<string>>>
    daySourcesRef: React.MutableRefObject<Record<string, DaySourceEntry>>
    userId: number
    localEventUpdateId: number | undefined
    isBookPeriodMode: boolean
    isOutlookFlow: boolean
    searchRegex: RegExp | undefined
} | null>(null)

type Props = Readonly<
    PropsWithChildren<{
        timelineResources: TimelineCalendarResource[]
        allServiceCenterIdsForEvents: readonly string[]
        userId: number
        localEventUpdateId: number | null | undefined
        isBookPeriodMode: boolean
        isOutlookFlow: boolean
        searchRegex: RegExp | undefined
    }>
>

export function TimelineContextProvider({
    timelineResources,
    allServiceCenterIdsForEvents,
    userId,
    localEventUpdateId,
    isBookPeriodMode,
    isOutlookFlow,
    searchRegex,
    children,
}: Props) {
    const resourceBufferMap = useMemo(() => {
        return timelineResources.reduce((acc, resource) => {
            acc[resource.id] = resource.buffer || 0
            return acc
        }, {} as Record<string, number>)
    }, [timelineResources])

    const eventIndexRef = useRef<Record<string, TimelineCalendarEvent[]>>({})
    // what each (service center, day) source delivered last time, keyed `${serviceCenterId}|${day}`
    const sourceEventIdsRef = useRef<Record<string, ReadonlySet<string>>>({})
    // keyed by FullCalendar event source id
    const daySourcesRef = useRef<Record<string, DaySourceEntry>>({})

    const value = useMemo(
        () => ({
            timelineResources,
            allServiceCenterIdsForEvents,
            resourceBufferMap,
            eventIndexRef,
            sourceEventIdsRef,
            daySourcesRef,
            userId,
            localEventUpdateId: localEventUpdateId ?? undefined,
            isBookPeriodMode,
            isOutlookFlow,
            searchRegex,
        }),
        [
            timelineResources,
            allServiceCenterIdsForEvents,
            resourceBufferMap,
            userId,
            localEventUpdateId,
            isBookPeriodMode,
            isOutlookFlow,
            searchRegex,
        ],
    )

    return <TimelineContext.Provider value={value}>{children}</TimelineContext.Provider>
}

export function useTimelineContext() {
    const context = useContext(TimelineContext)

    // If the hook is used outside of the Provider, context will be null
    if (!context) {
        throw new Error(
            "useTimelineContext must be used within a TimelineContextProvider. " +
                "Check that you have wrapped your component " +
                "in <TimelineContextProvider timelineResources={...}>.",
        )
    }

    return context
}
